"use client";

// Barras, cabecera, menú y carrito lateral del tema dopamina. Solo lectura.
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Check, Lock, Minus, Plus, ShoppingBag, Trash2, Truck, X } from "lucide-react";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import type { ProductVariant } from "@/dropai/types";
import { store } from "@/lib/store";
import type { BarMessage, ThemeContent } from "../types";
import { closeCart, openCart, payHref, removeLine, setUnits, useCart } from "./cart";
import { deliveryWindow, shortDate, spainNow } from "./delivery";
import { SlotPhoto } from "./parts";
import { euros, FOCUS, PRIMARY, priceForUnits } from "./ui";

const variants: readonly ProductVariant[] = store.product.variants ?? [];
const noSubscription = () => () => {};
const useMounted = () => useSyncExternalStore(noSubscription, () => true, () => false);
const useCalm = () => useSyncExternalStore(noSubscription, () => window.matchMedia("(prefers-reduced-motion: reduce)").matches, () => true);

const DAYS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
/** «mar 3 nov» en hora de Madrid: igual en el servidor y en el navegador (sin saltos al hidratar). */
export function endsLabel(iso: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Madrid", month: "numeric", day: "numeric", weekday: "short" }).formatToParts(new Date(iso));
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? "";
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(part("weekday"));
  return DAYS[Math.max(0, weekday)] + " " + String(Number(part("day"))) + " " + (MONTHS[Number(part("month")) - 1] ?? "");
}

/** La cuenta atrás de una fecha de fin real (nunca se reinicia por visitante): días, horas, minutos y segundos. */
function Countdown({ endsAt }: { endsAt: string }) {
  const mounted = useMounted();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  if (!mounted) return <span className="font-extrabold">{"el " + endsLabel(endsAt)}</span>;
  const left = Math.max(0, new Date(endsAt).getTime() - now);
  const units: [number, string][] = [
    [Math.floor(left / 86400000), "días"],
    [Math.floor(left / 3600000) % 24, "h"],
    [Math.floor(left / 60000) % 60, "min"],
    [Math.floor(left / 1000) % 60, "s"],
  ];
  return (
    <span className="inline-flex items-center gap-1" role="timer" aria-label={"Acaba el " + endsLabel(endsAt)}>
      {units.map(([value, unit], index) => (
        <span key={unit} className="inline-flex items-center gap-1">
          {index > 0 ? <span aria-hidden="true" className="font-bold">:</span> : null}
          <span className="inline-flex min-w-[1.875rem] flex-col items-center rounded-[4px] bg-white px-1 py-0.5 leading-none text-[#1b1530] tabular-nums">
            <span className="text-[0.8125rem] font-extrabold">{String(value).padStart(2, "0")}</span>
            <span className="text-[0.5rem] font-bold uppercase">{unit}</span>
          </span>
        </span>
      ))}
    </span>
  );
}

function Message({ message }: { message: BarMessage }) {
  const mounted = useMounted();
  let text: string = message.text;
  if (message.kind === "delivery" && message.days && mounted) {
    const window_ = deliveryWindow(spainNow(), message.days[0], message.days[1]);
    const from = shortDate(window_.from);
    const to = shortDate(window_.to);
    text = from === to ? "Pide hoy y llega el " + from : "Pide hoy y llega antes del " + to;
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span aria-hidden="true">{message.emoji}</span>
      <span>{text}</span>
      {message.kind === "offer" && message.endsAt ? <Countdown endsAt={message.endsAt} /> : null}
    </span>
  );
}

/**
 * La barra de anuncios (8.3) con el degradado de la tienda que se desplaza (≥ 8 s). Con dos mensajes:
 * en ordenador, los dos; en móvil, se turnan cada 4 s. Con movimiento reducido, quietos.
 */
export function AnnouncementBar({ messages }: { messages: BarMessage[] }) {
  const calm = useCalm();
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (messages.length < 2 || calm) return;
    const timer = window.setInterval(() => setIndex((current) => (current + 1) % messages.length), 4000);
    return () => window.clearInterval(timer);
  }, [messages.length, calm]);
  const current = messages[index] ?? messages[0];
  return (
    <div className="wl-grad-bar relative z-40 text-[0.8125rem] font-bold lg:text-sm">
      <div className="mx-auto flex min-h-[2.75rem] max-w-[75rem] items-center justify-center gap-8 px-4 py-1.5 text-center lg:min-h-[3.25rem]">
        <p key={current.text} aria-live="polite" className="wl-swap lg:hidden">
          <Message message={current} />
        </p>
        {messages.map((message, position) => (
          <p key={message.text} className={"hidden lg:block" + (position > 0 ? " border-l border-current/30 pl-8" : "")}>
            <Message message={message} />
          </p>
        ))}
      </div>
    </div>
  );
}

/** La barra oscura de confianza bajo la cabecera (8.3): 1-2 datos verdaderos. */
export function TrustBar({ items }: { items: { emoji: string; text: string }[] }) {
  if (items.length === 0) return null;
  return (
    <div className="wl-dark text-[0.8125rem] font-bold lg:text-sm">
      <ul className="mx-auto flex min-h-[2.5rem] max-w-[75rem] items-center justify-center gap-x-6 gap-y-1 px-4 py-1.5">
        {items.map((item) => (
          <li key={item.text} className="flex items-center gap-1.5">
            <span aria-hidden="true">{item.emoji}</span>
            {item.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CartButton() {
  const cart = useCart();
  const count = cart.line?.units ?? 0;
  return (
    <Link href="/carrito" onClick={(event) => { event.preventDefault(); openCart(); }} aria-label={count > 0 ? "Abrir el carrito, " + String(count) + (count === 1 ? " producto" : " productos") : "Abrir el carrito, vacío"} className={"relative flex size-11 items-center justify-center rounded-full transition-transform duration-150 active:scale-95 " + FOCUS}>
      <ShoppingBag aria-hidden="true" className="size-6" strokeWidth={1.9} />
      {count > 0 ? (
        <span key={cart.added} className="wl-grad-accent wl-bump absolute -top-0.5 -right-0.5 flex size-5 items-center justify-center rounded-full text-[0.6875rem] font-extrabold tabular-nums">
          {String(count)}
        </span>
      ) : null}
    </Link>
  );
}

/** El menú lateral del móvil (5.4): enlaces, botón de compra y sellos. */
function MenuPanel({ content, open, onClose }: { content: ThemeContent; open: boolean; onClose: () => void }) {
  const sales = content.sales!;
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    close.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  const links = [{ href: "/", label: "Inicio" }, ...sales.nav, { href: "/envios", label: "Envíos" }, { href: "/sobre-nosotros", label: "Sobre nosotros" }];
  return (
    <>
      <div aria-hidden="true" onClick={onClose} data-open={open} className={"wl-fade fixed inset-0 z-50 bg-[rgb(5_6_26/0.45)] " + (open ? "visible opacity-100" : "invisible opacity-0")} />
      <div id="menu-movil" role="dialog" aria-modal="true" aria-label="Menú" data-open={open} className={"wl-drawer wl-glass fixed inset-y-0 left-0 z-50 flex w-[min(22rem,88vw)] flex-col rounded-r-[24px] bg-(--wl-surface) p-5 text-(--wl-ink) " + (open ? "visible translate-x-0" : "invisible -translate-x-full")}>
        <div className="flex items-center justify-between">
          <span className="font-display text-xl [font-weight:var(--wl-display-weight)]">{store.name}</span>
          <button ref={close} type="button" onClick={onClose} aria-label="Cerrar el menú" className={"flex size-11 items-center justify-center rounded-full " + FOCUS}>
            <X aria-hidden="true" className="size-6" />
          </button>
        </div>
        <nav aria-label="Menú" className="mt-6">
          <ul className="grid gap-1">
            {links.map((link) => (
              <li key={link.href}>
                <Link href={link.href} onClick={onClose} className={"flex min-h-12 items-center rounded-[12px] px-3 text-lg font-bold hover:bg-(--wl-accent-soft) " + FOCUS}>{link.label}</Link>
              </li>
            ))}
          </ul>
        </nav>
        <Link href={"/producto/" + sales.slug} onClick={onClose} className={PRIMARY + " mt-6 w-full"}>{sales.cta}</Link>
        <ul className="mt-6 grid gap-3 text-sm font-semibold">
          {sales.riskFree.map((item) => {
            const Icon = item.icon === "truck" ? Truck : Lock;
            return <li key={item.icon} className="flex items-center gap-2"><Icon aria-hidden="true" className="size-5 text-(--wl-accent-text)" />{item.title}</li>;
          })}
        </ul>
      </div>
    </>
  );
}

/** La cabecera fija (5.4): en móvil, hamburguesa, logo y carrito; en ordenador, logo, enlaces y carrito. */
export function Header({ content }: { content: ThemeContent }) {
  const sales = content.sales!;
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  return (
    <header className="wl-glass sticky top-0 z-40 rounded-none border-x-0 border-t-0 text-(--wl-ink)">
      <div className="mx-auto grid h-16 max-w-[75rem] grid-cols-[2.75rem_1fr_2.75rem] items-center gap-2 px-3 lg:h-[4.5rem] lg:grid-cols-[auto_1fr_auto] lg:gap-8 lg:px-10">
        <button type="button" onClick={() => setOpen(true)} aria-expanded={open} aria-controls="menu-movil" aria-label="Abrir el menú" className={"wl-burger flex size-11 flex-col items-center justify-center gap-[5px] rounded-full lg:hidden " + FOCUS}>
          <span className="block h-[2px] w-5 rounded-full bg-current" />
          <span className="block h-[2px] w-5 rounded-full bg-current" />
          <span className="block h-[2px] w-5 rounded-full bg-current" />
        </button>
        <Link href="/" className={"justify-self-center font-display text-[1.5rem] leading-none tracking-[-0.01em] lg:justify-self-start lg:text-[1.75rem] [font-weight:var(--wl-display-weight)] " + FOCUS}>{store.name}</Link>
        <nav aria-label="Principal" className="hidden lg:block">
          <ul className="flex items-center justify-end gap-7 text-[0.9375rem] font-semibold">
            {sales.nav.map((link) => (
              <li key={link.href}>
                <Link href={link.href} aria-current={pathname === link.href ? "page" : undefined} className={"relative py-2 after:absolute after:inset-x-0 after:-bottom-0.5 after:h-0.5 after:origin-left after:scale-x-0 after:rounded-full after:bg-(--wl-accent) after:transition-transform after:duration-300 hover:after:scale-x-100 aria-[current=page]:after:scale-x-100 " + FOCUS}>{link.label}</Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="justify-self-end">
          <CartButton />
        </div>
      </div>
      <MenuPanel content={content} open={open} onClose={() => setOpen(false)} />
    </header>
  );
}

/** Lo que hay en el carrito, para el lateral y para /carrito. */
export function CartBody({ content, page = false }: { content: ThemeContent; page?: boolean }) {
  const sales = content.sales!;
  const cart = useCart();
  const line = cart.line;
  if (line === null) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 py-10 text-center">
        <ShoppingBag aria-hidden="true" className="size-12 text-(--wl-accent-text)" strokeWidth={1.5} />
        <p className="text-lg font-bold">Tu carrito está vacío</p>
        <p className="max-w-[22rem] text-(--wl-ink-muted)">{sales.promise}</p>
        <Link href={"/producto/" + sales.slug} onClick={page ? undefined : closeCart} className={PRIMARY + " w-full max-w-[22rem]"}>{sales.cta}</Link>
      </div>
    );
  }
  const total = priceForUnits(line.units, sales.packs, store.product.price);
  const full = Math.round(store.product.price * line.units * 100) / 100;
  const gift = sales.packs.filter((pack) => pack.gift && line.units >= pack.units).at(-1)?.gift;
  const options = Object.values(line.options).filter(Boolean).join(" · ");
  return (
    <div className="flex flex-1 flex-col">
      <div className="flex gap-4 border-b border-(--wl-line) pb-5">
        <div className="relative size-24 shrink-0 overflow-hidden rounded-[14px] bg-(--wl-photo-bg)">
          {content.media ? <SlotPhoto slot={content.media.featured} sizes="96px" compact /> : null}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-bold leading-snug">{store.name + " · " + sales.descriptor}</p>
          {options ? <p className="mt-0.5 text-sm text-(--wl-ink-muted)">{options}</p> : null}
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="flex items-center rounded-full border border-(--wl-line)">
              <button type="button" onClick={() => setUnits(line.units - 1)} aria-label="Quitar una unidad" className={"flex size-11 items-center justify-center rounded-full " + FOCUS}><Minus aria-hidden="true" className="size-4" /></button>
              <span className="min-w-6 text-center font-bold tabular-nums" aria-live="polite">{String(line.units)}</span>
              <button type="button" onClick={() => setUnits(line.units + 1)} aria-label="Añadir una unidad" className={"flex size-11 items-center justify-center rounded-full " + FOCUS}><Plus aria-hidden="true" className="size-4" /></button>
            </div>
            <div className="text-right">
              <p className="font-extrabold tabular-nums">{euros(total)}</p>
              {full > total ? <p className="text-xs text-(--wl-ink-muted) line-through tabular-nums">{euros(full)}</p> : null}
            </div>
          </div>
          <button type="button" onClick={removeLine} className={"mt-2 inline-flex min-h-11 items-center gap-1 text-sm text-(--wl-ink-muted) underline-offset-4 hover:underline " + FOCUS}>
            <Trash2 aria-hidden="true" className="size-4" />Quitar
          </button>
        </div>
      </div>
      {gift ? (
        <p className="mt-4 flex items-center gap-2 rounded-[12px] bg-(--wl-accent-soft) px-4 py-3 text-sm font-bold">
          <span aria-hidden="true">🎁</span>
          {"Regalo incluido: " + gift}
        </p>
      ) : null}
      <div className="mt-auto pt-6">
        <div className="flex items-baseline justify-between text-lg font-extrabold">
          <span>Total</span>
          <span className="tabular-nums">{euros(total)}</span>
        </div>
        <p className="mt-1 text-sm text-(--wl-ink-muted)">{sales.priceNote}</p>
        <Link href={payHref(variants, line)} onClick={closeCart} className={PRIMARY + " mt-4 w-full"}>
          <Lock aria-hidden="true" className="size-5" />
          IR AL PAGO SEGURO
        </Link>
        {page ? null : (
          <button type="button" onClick={closeCart} className={"mt-3 min-h-11 w-full rounded-full text-sm font-bold underline-offset-4 hover:underline " + FOCUS}>Seguir comprando</button>
        )}
      </div>
    </div>
  );
}

/** El carrito lateral (5.1): se abre al añadir, con el check y el contador que salta (7.5). */
export function CartDrawer({ content }: { content: ThemeContent }) {
  const cart = useCart();
  const close = useRef<HTMLButtonElement>(null);
  // Al navegar (el pago, otra página) el cajón se cierra: el layout no se vuelve a montar y el fondo oscuro taparía el formulario.
  const pathname = usePathname();
  useEffect(() => {
    closeCart();
  }, [pathname]);
  useEffect(() => {
    if (!cart.open) return;
    close.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeCart();
    };
    window.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [cart.open]);
  return (
    <>
      <div aria-hidden="true" onClick={closeCart} data-open={cart.open} className={"wl-fade fixed inset-0 z-50 bg-[rgb(5_6_26/0.45)] " + (cart.open ? "visible opacity-100" : "invisible opacity-0")} />
      <div role="dialog" aria-modal="true" aria-labelledby="carrito-t" data-open={cart.open} className={"wl-drawer fixed inset-y-0 right-0 z-50 flex w-[min(26rem,100vw)] flex-col bg-(--wl-surface) p-5 text-(--wl-ink) shadow-[-24px_0_60px_rgb(0_0_0/0.18)] sm:rounded-l-[24px] " + (cart.open ? "visible translate-x-0" : "invisible translate-x-full")}>
        <div className="flex items-center justify-between">
          <h2 id="carrito-t" className="font-display text-2xl [font-weight:var(--wl-display-weight)]">Tu carrito</h2>
          <button ref={close} type="button" onClick={closeCart} aria-label="Cerrar el carrito" className={"flex size-11 items-center justify-center rounded-full " + FOCUS}><X aria-hidden="true" className="size-6" /></button>
        </div>
        {cart.added > 0 && cart.line ? (
          <p key={cart.added} role="status" className="wl-pop mt-3 mb-4 flex items-center gap-2 rounded-[12px] bg-(--wl-accent-soft) px-4 py-3 font-bold">
            <span className="wl-grad-accent flex size-7 items-center justify-center rounded-full"><Check aria-hidden="true" className="size-4" strokeWidth={3} /></span>
            Añadido. Buena elección.
          </p>
        ) : (
          <div className="mt-4" />
        )}
        <CartBody content={content} />
      </div>
    </>
  );
}
